import { setUserId } from "@snowplow/browser-tracker"

/**
 * The account this browser is signed in as, as last reported by Supabase.
 *
 * Kept here rather than only handed to the tracker, because `setUserId`
 * dispatches to *registered* trackers: called before `newTracker` has run it
 * iterates an empty list and is dropped silently — no error, no retry, and the
 * listener will not fire again during a short visit.
 *
 * That ordering is the normal one on a page load restoring a cached session.
 * The auth listener is registered while `helpers/supabase` is still being
 * imported, and supabase-js can emit `INITIAL_SESSION` before React has
 * mounted and created the tracker. Returning from Stripe is exactly that load,
 * and it is the one where the purchase happens.
 */
let signedInUserId: string | undefined

/**
 * Records the account and tells the tracker, if there is one yet.
 *
 * Passing `undefined` is a sign-out and clears the tracker too, which is the
 * one case that has to reach it immediately rather than wait for a page view.
 */
export function rememberTrackerUserId(userId: string | undefined): void {
  signedInUserId = userId
  setUserId(userId)
}

/**
 * Re-applies the remembered account to the tracker.
 *
 * Called from the page view, which can only run once a tracker exists, so it
 * closes the window where `rememberTrackerUserId` arrived too early.
 *
 * Does nothing when no account has been reported. The sign-up and sign-in
 * paths call `setUserId` directly and can get there first, so blanking the
 * tracker here would undo them.
 */
export function applyTrackerUserId(): void {
  if (!signedInUserId) return

  setUserId(signedInUserId)
}
